import { useEffect, useRef, useState } from "react";

import ContextoService from "../../../../../../services/ContextoService";
import SesionsService from "../../../../../../services/SesionsService";
import UnidadService from "../../../../../../services/UnidadService";
import { useAuth } from "../../../../../../context/AuthContext";

import { ROWS_PER_PAGE } from "./contextStep.styles";

const MAX_CONTEXT_PDF_SIZE_BYTES = 5 * 1024 * 1024;

const useContextStep = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [checkingSessions, setCheckingSessions] = useState(false);
  const [openGenerateConfirm, setOpenGenerateConfirm] = useState(false);
  const [openSessionsBlocked, setOpenSessionsBlocked] = useState(false);
  const [willOverwriteExistingContext, setWillOverwriteExistingContext] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const nombreIE = user?.institucion?.nombre;
  const institucionIdActiva = user?.institucion?.id || null;
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [unidades, setUnidades] = useState([]);
  const [loadingUnidades, setLoadingUnidades] = useState(true);
  const [page, setPage] = useState(0);
  const fileInputRef = useRef(null);

  const hasExistingSessions = async () => {
    const res = await SesionsService.existeEnInstitucionActiva("SAC");
    return Boolean(res?.data?.exists);
  };

  const hasExistingContext = async () => {
    return ContextoService.existeContextoGenerado();
  };

  useEffect(() => {
    const cargarUnidades = async () => {
      setLoadingUnidades(true);
      try {
        if (!institucionIdActiva) {
          setUnidades([]);
          return;
        }

        const res = await UnidadService.obtenerUnidades({
          idInstitucionEdu: institucionIdActiva,
          skip: 0,
          limit: 100,
        });

        setUnidades(res.items || []);
        setPage(0);
      } catch (error) {
        console.error("Error cargando unidades:", error);
        setUnidades([]);
      } finally {
        setLoadingUnidades(false);
      }
    };

    cargarUnidades();
  }, [institucionIdActiva]);

  const handleGenerarContexto = async () => {
    setLoading(true);
    try {
      await ContextoService.GenerarContexto(selectedPdfFile);
      setSnackbarMsg("¡El contexto fue generado con éxito!");
      setSnackbarType("success");
    } catch (error) {
      setSnackbarMsg(
        error?.response?.data?.detail || "Error al conectarse con el backend."
      );
      setSnackbarType("error");
    } finally {
      setOpenSnackbar(true);
      setLoading(false);
    }
  };

  const handleRequestGenerarContexto = async () => {
    if (loading || checkingSessions) return;

    if (!institucionIdActiva) {
      setSnackbarMsg("No se pudo identificar la institución activa para validar las sesiones.");
      setSnackbarType("error");
      setOpenSnackbar(true);
      return;
    }

    setCheckingSessions(true);
    try {
      const existenSesiones = await hasExistingSessions();

      if (existenSesiones) {
        setOpenSessionsBlocked(true);
        return;
      }

      const existeContextoGenerado = await hasExistingContext();
      setWillOverwriteExistingContext(existeContextoGenerado);
      setOpenGenerateConfirm(true);
    } catch (error) {
      console.error("Error validando sesiones antes de generar contexto:", error);
      setSnackbarMsg("No se pudo validar si ya existen sesiones o contexto generado.");
      setSnackbarType("error");
      setOpenSnackbar(true);
    } finally {
      setCheckingSessions(false);
    }
  };

  const handleRequestContinue = async (onNext) => {
    if (loading || checkingSessions) return;

    if (!institucionIdActiva) {
      onNext();
      return;
    }

    setCheckingSessions(true);
    try {
      const existenSesiones = await hasExistingSessions();

      if (existenSesiones) {
        setOpenSessionsBlocked(true);
        return;
      }

      onNext();
    } catch (error) {
      console.error("Error validando sesiones antes de continuar:", error);
      setSnackbarMsg("No se pudo validar si ya existen sesiones creadas.");
      setSnackbarType("error");
      setOpenSnackbar(true);
    } finally {
      setCheckingSessions(false);
    }
  };

  const handleCloseGenerateConfirm = () => {
    if (loading) return;
    setOpenGenerateConfirm(false);
    setWillOverwriteExistingContext(false);
  };

  const handleConfirmGenerarContexto = async () => {
    setOpenGenerateConfirm(false);
    setWillOverwriteExistingContext(false);
    await handleGenerarContexto();
  };

  const handleCloseSessionsBlocked = () => {
    setOpenSessionsBlocked(false);
  };

  const handleDescargarContexto = async () => {
    setLoading(true);
    try {
      const res = await ContextoService.DescargarContexto("pdf");
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Contexto_SAC.pdf";
      link.click();
      URL.revokeObjectURL(url);

      setSnackbarMsg("Archivo descargado correctamente");
      setSnackbarType("success");
    } catch {
      setSnackbarMsg("Error al descargar el archivo");
      setSnackbarType("error");
    } finally {
      setOpenSnackbar(true);
      setLoading(false);
    }
  };

  const handlePdfSelected = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    const esPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!esPdf) {
      setSelectedPdfFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSnackbarMsg("Solo se permiten archivos PDF.");
      setSnackbarType("error");
      setOpenSnackbar(true);
      return;
    }

    if (file.size > MAX_CONTEXT_PDF_SIZE_BYTES) {
      setSelectedPdfFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSnackbarMsg("El archivo PDF supera el tamaño máximo permitido de 5 MB.");
      setSnackbarType("error");
      setOpenSnackbar(true);
      return;
    }

    setSelectedPdfFile(file);
  };

  const handleRemoveSelectedPdf = () => {
    setSelectedPdfFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return {
    ROWS_PER_PAGE,
    authLoading,
    checkingSessions,
    fileInputRef,
    loading,
    loadingUnidades,
    nombreIE,
    openGenerateConfirm,
    openSnackbar,
    openSessionsBlocked,
    page,
    setOpenGenerateConfirm,
    setPage,
    setOpenSnackbar,
    setOpenSessionsBlocked,
    selectedPdfFile,
    snackbarMsg,
    snackbarType,
    unidades,
    willOverwriteExistingContext,
    handleCloseGenerateConfirm,
    handleCloseSessionsBlocked,
    handleConfirmGenerarContexto,
    handleDescargarContexto,
    handleGenerarContexto,
    handlePdfSelected,
    handleRemoveSelectedPdf,
    handleRequestContinue,
    handleRequestGenerarContexto,
  };
};

export default useContextStep;
